// Reloj de interacción de la ficha (2026-10-19, docs/bupa/medir-tiempo-registro.md).
//
// El reloj de fase (`avanzarRelojFase` en fichaFases.js) mide cuánto está una
// parte de la ficha en PANTALLA, y eso es un techo: una ficha olvidada en el
// bolsillo durante la colación suma igual. Para la reunión con la subgerencia
// de Enfermería de Bupa, Sebastián pidió medir el tiempo en que la enfermera
// de verdad trabaja en el flujo ("contar el tiempo que una enfermera clickea
// dentro del flujo"). Este reloj corre solo entre gestos seguidos: si pasa más
// de un minuto sin tocar, escribir ni dictar, el reloj se pausa hasta el
// próximo gesto. Y cuenta toques y teclas.
//
// Máquina de estados PURA, como avanzarRelojFase: la ficha la alimenta desde
// un ref (patrón #523, jamás por render) y la cierra cada vez que emite
// `fase_visitada`. Solo números: el tipo de gesto es catálogo cerrado y nunca
// entra qué tecla, qué campo ni qué se escribió.

import { nuevaRef } from './benitoSenales';

// Un minuto sin gestos es una pausa: leer un hallazgo o pensar la redacción
// cabe en ese rato, y salir de la pieza con el teléfono en el bolsillo no.
export const PAUSA_MS = 60_000;

// 'toque' = un toque que el navegador entiende como clic (no cuenta el gesto
// de desplazar, que no dispara clic). 'tecla' = un cambio en un campo de texto.
// 'voz' = el micrófono sigue grabando (dictar es registrar sin tocar).
// 'actividad' = cualquier otro gesto sobre la ficha (apoyar el dedo para
// desplazar, la rueda del mouse): mantiene vivo el reloj sin sumar toques.
export const TIPOS_INTERACCION = Object.freeze(['toque', 'tecla', 'voz', 'actividad']);

export const RELOJ_INTERACCION_INICIAL = Object.freeze({ activoMs: 0, toques: 0, teclas: 0, ultima: null });

const entero = (n) => (Number.isFinite(n) && n > 0 ? n : 0);

export function registrarInteraccion(reloj, tipo, ahora) {
  const actual = reloj && typeof reloj === 'object' ? reloj : RELOJ_INTERACCION_INICIAL;
  if (!TIPOS_INTERACCION.includes(tipo) || !Number.isFinite(ahora)) return actual;
  const ultima = Number.isFinite(actual.ultima) ? actual.ultima : null;
  const hueco = ultima == null ? null : ahora - ultima;
  // El primer gesto de un tramo no suma: no se sabe desde cuándo estaba ahí.
  // Un reloj de sistema que retrocede (hueco negativo) tampoco resta.
  const suma = hueco != null && hueco > 0 && hueco <= PAUSA_MS ? hueco : 0;
  return {
    activoMs: entero(actual.activoMs) + suma,
    toques: entero(actual.toques) + (tipo === 'toque' ? 1 : 0),
    teclas: entero(actual.teclas) + (tipo === 'tecla' ? 1 : 0),
    ultima: ahora,
  };
}

// Lo medido desde el último cierre, y el reloj en cero para la fase que sigue.
// `ultima` sobrevive al cierre: el rato entre el último gesto de una fase y el
// primero de la siguiente es trabajo continuo y queda en la fase de llegada.
export function cerrarReloj(reloj) {
  const actual = reloj && typeof reloj === 'object' ? reloj : RELOJ_INTERACCION_INICIAL;
  return {
    medida: {
      activos: Math.round(entero(actual.activoMs) / 1000),
      toques: entero(actual.toques),
      teclas: entero(actual.teclas),
    },
    reloj: { ...RELOJ_INTERACCION_INICIAL, ultima: Number.isFinite(actual.ultima) ? actual.ultima : null },
  };
}

// Tocar el texto de un <label> hace que el navegador le mande al control un
// SEGUNDO clic sintético (así funciona un label). Sin esto, "¿Tiene alergias?"
// contaba dos toques por uno (revisión del 19-oct). Se reconoce porque llega
// casi en el mismo instante y su control tiene ese label, que es o contiene
// donde cayó el clic anterior.
export const CLIC_LABEL_MS = 100;
export function esClicReenviadoPorLabel(objetivo, anterior, ahora) {
  if (!objetivo || !anterior || !anterior.target) return false;
  if (!Number.isFinite(ahora) || !Number.isFinite(anterior.t)) return false;
  const hueco = ahora - anterior.t;
  if (hueco < 0 || hueco > CLIC_LABEL_MS) return false;
  const labels = objetivo.labels;
  if (!labels || typeof labels.length !== 'number') return false;
  return Array.from(labels).some(
    (l) => l === anterior.target || (typeof l.contains === 'function' && l.contains(anterior.target)),
  );
}

// ¿El gesto cayó en un campo donde se escribe? Un cambio en una casilla, un
// botón de opción o una lista desplegable no es escribir (también dispara el
// evento de cambio, y ya cuenta como toque).
const TIPOS_TEXTO = new Set(['', 'text', 'search', 'number', 'tel', 'email', 'url']);
export function esCampoDeTexto(el) {
  if (!el || typeof el !== 'object') return false;
  if (el.isContentEditable) return true;
  const tag = typeof el.tagName === 'string' ? el.tagName.toLowerCase() : '';
  if (tag === 'textarea') return true;
  if (tag === 'input') return TIPOS_TEXTO.has(String(el.getAttribute?.('type') || '').toLowerCase());
  return false;
}

// Un número al azar por paciente para sumar sus fases sin contar pacientes a
// ojo. NO es el id del registro ni sale de él (el patrón de `ref` de
// benitoSenales.js): sirve solo para juntar eventos que ya eran de la misma
// persona. Vive en este teléfono y se borra junto con el registro, al vencer
// el turno, al borrar la cama o al descartar el turno (`borrarMarcasLocales`
// en registroTurno.js).
export const claveFichaRef = (id) => `mipae:turno:fichaRef:${id}`;
export function refDeFicha(id) {
  if (!id) return null;
  try {
    const guardada = localStorage.getItem(claveFichaRef(id));
    if (guardada) return guardada;
    const nueva = nuevaRef();
    if (nueva) localStorage.setItem(claveFichaRef(id), nueva);
    return nueva;
  } catch {
    return null;
  }
}

// Interruptor para apagarlo sin tocar el código. Apagado, `fase_visitada`
// vuelve a su payload de siempre.
export function relojInteraccionActivo() {
  return process.env.REACT_APP_RELOJ_INTERACCION_ENABLED !== 'false';
}
